import * as React from "react";
import { useParams } from "react-router-dom";
import { Progress, Separator, Skeleton } from "randmarcomps";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useGetV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberQuery } from "randmar-api-client";
import { IPurchaseOrderResult, IReceiptResult, IRequisitionResult } from "../api/document/DocumentsApi";
import PurchaseOrderView from "../components/document/PurchaseOrderView";
import ReceiptView from "../components/document/ReceiptView";
import RequisitionView from "../components/document/RequisitionView";
import DocumentActions from "../components/document/actions/DocumentActions";
import ButtonWithActions from "../components/util/ButtonWithActions";
import { useManufacturer } from "../contexts/WithManufacturer";
import { PurchaseOrder, Receipt, Requisition } from "../stores/document/Document";

const handleDocumentName = (docNum: string) => {
  if (docNum.startsWith("PO") && docNum.length === 9) return "Purchase Order";
  if (docNum.startsWith("RQ") && docNum.length === 9) return "Draft Purchase";
  if (docNum.startsWith("RC") && docNum.length === 9) return "Reception";
  return "Document";
};

const Document: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { documentNumber = "" } = useParams<{ documentNumber: string }>();

  const { data: document, isLoading } = useGetV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberQuery(
    applicationId && documentNumber ? { routeManufacturerId: applicationId, documentNumber } : skipToken
  );

  if (isLoading) {
    return (
      <main>
        <Progress value={100} />
        <div className="flex justify-between items-center my-4">
          <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
            <Skeleton className="h-8 w-48 rounded-md" />
            <Separator orientation="vertical" className="hidden md:block h-8" />
            <Skeleton className="h-7 w-32 rounded-md" />
          </div>
          <Skeleton className="h-10 w-24 rounded-md" />
        </div>
        <Skeleton className="h-[400px] w-full rounded-xl bg-white" />
      </main>
    );
  }

  if (!document) {
    return (
      <main>
        <Progress value={100} />
        <div className="my-4 flex h-[50vh] items-center justify-center rounded-lg border-2 border-dashed bg-white">
          <div className="text-center">
            <h2 className="text-xl font-semibold">Document Not Found</h2>
            <p className="mt-2 text-sm text-gray-500">The document you are looking for does not exist or you do not have permission to view it.</p>
          </div>
        </div>
      </main>
    );
  }

  let documentView;

  if (documentNumber.startsWith("PO") && documentNumber.length === 9) {
    documentView = <PurchaseOrderView purchaseOrder={PurchaseOrder.convertFrom(document as IPurchaseOrderResult)} />;
  } else if (documentNumber.startsWith("RQ") && documentNumber.length === 9) {
    documentView = <RequisitionView requisition={Requisition.convertFrom(document as IRequisitionResult)} />;
  } else if (documentNumber.startsWith("RC") && documentNumber.length === 9) {
    documentView = <ReceiptView receipt={Receipt.convertFrom(document as IReceiptResult)} />;
  }

  return (
    <main>
      <Progress value={100} />

      <div className="flex justify-between items-center my-4">
        <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
          <h1 className="text-2xl font-bold">{handleDocumentName(documentNumber)}</h1>
          <Separator orientation="vertical" className="hidden md:block h-8" />
          <h2 className="text-xl font-mono">{documentNumber}</h2>
        </div>
        <ButtonWithActions buttonText="Actions">
          <DocumentActions documentNumber={documentNumber} />
        </ButtonWithActions>
      </div>

      {documentView}
    </main>
  );
};

export default Document;
